import React, { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";
import { Link } from "react-router-dom";

import { DateTime } from "luxon";

import Calendar2 from "../Calendar2";
import DelayInput from "../DelayInput";
import HealthFairEditNoticesDialog from "./HealthFairEditNoticesDialog";
import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";

import { apiGet, V2 } from "../../Api";
import { formatName, TZ } from "../../Utilities";

import { IAuthProps, WellKnownGroups } from "../AuthContext";
import HasRight from "../HasRight";
import ViewToolbar from "../ViewToolbar";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../blocks.css";
import "../../icomoon.css";
import "../../spinner.css";
import "../viewer.css";
import withProps from "../withProps";

interface IHealthFairScheduleViewState {
    date: number;
    month: string;
    now: Date;
    events: { [day: number]: any[] };
    isLoading: boolean | string;
    isEditNoticesDialogOpen: boolean;
    isSelectionMode: boolean;

    searchPerson: string;

    displayBefore: DateTime;
    displayAfter: DateTime;

    people: any[];

    [name: string]: any;
}

interface IHealthFairSelectionViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
    isSelectionMode?: boolean;
}

class HealthFairScheduleView extends React.Component<IHealthFairSelectionViewProps, IHealthFairScheduleViewState> {
    private refreshTimer: number | undefined;

    constructor(props: Readonly<IHealthFairSelectionViewProps>) {
        super(props);

        const today = DateTime.local();

        let month = today.toFormat("yyyy-MM");
        let searchPerson = (this.props.authContext.isInGroup(WellKnownGroups.HfTeam) ? "caller" : "");

        if (props.location.state) {
            // Show last shown month
            month = props.location.state.month || month;
            searchPerson = props.location.state.searchPerson;
        }

        this.state = {
            date: 1,
            events: {},
            isLoading: true,
            isEditNoticesDialogOpen: false,
            month,
            now: new Date(),
            isSelectionMode: (props.location.state && props.location.state.selectionMode) || props.isSelectionMode || false,

            searchPerson,

            displayBefore: today.endOf("month"),
            displayAfter: today.startOf("month"),

            people: [],
        };

        this.viewDayDetails = this.viewDayDetails.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
        this.displayRangeChanged = this.displayRangeChanged.bind(this);
        this.showEditNoticesDialog = this.showEditNoticesDialog.bind(this);
        this.closeEditNoticesDialog = this.closeEditNoticesDialog.bind(this);
    }

    public async componentDidMount() {
        try {
            const marketers = await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.HfTeam}`);

            this.setState({
                people: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }

        this.refreshTimer = window.setInterval(() => this.requestEvents(true), 10 * 60 * 1000);
    }

    public componentWillUnmount() {
        if (this.refreshTimer) {
            window.clearInterval(this.refreshTimer);
            this.refreshTimer = undefined;
        }
    }

    public render() {
        return (
            <div className="viewer-root viewer-full full-height">
                <ViewToolbar>
                    <Link to="/fairs/search" className="icon-search" />
                    {!this.state.isSelectionMode
                        ? <>
                            <HasRight right="fair:create">
                                {!this.props.authContext.serverType ? <Link to="/fairtargets" className="icon-plus" /> : null}
                            </HasRight>
                            <OverflowMenuItem>
                                <ToolbarMenuItem right="fair:notice">
                                    <button type="button" onClick={this.showEditNoticesDialog}><i className="icon-info-circle" />Edit Notices...</button>
                                </ToolbarMenuItem>
                            </OverflowMenuItem>
                        </>
                        : null}
                </ViewToolbar>
                {this.state.isSelectionMode
                    ? <div className="status-box slim">
                        Use the calendar below to find a health fair.
                    </div>
                    : null}
                <HealthFairEditNoticesDialog isOpen={this.state.isEditNoticesDialogOpen} after={this.state.displayAfter} before={this.state.displayBefore} onRequestClose={() => this.closeEditNoticesDialog()} onSuccess={() => this.closeEditNoticesDialog(true)} />
                <div className="filter-control-box">
                    <DelayInput timeout={500} className="fill-space" type="month" name="month" onChangeDelayed={this.handleElementChange} value={this.state.month} />
                    <select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">Everyone</option>
                        <option value="caller">You</option>
                        {
                            this.state.people.map(a =>
                                <option key={a.id} value={a.id}>{formatName(a.name)}</option>
                            )
                        }
                    </select>
                </div>
                <div className="spinner-overlay-root full-height">
                    <Calendar2 className="full-height" displayMonth={this.state.month} highlightDate={this.state.now} events={this.state.events} onDaySelected={this.viewDayDetails} onDisplayRangeChanged={this.displayRangeChanged} />
                    {
                        this.state.isLoading ? (
                            <div className="spinner-overlay">
                                {
                                    (typeof this.state.isLoading === "string") ?
                                        <span className="error-text-big">{this.state.isLoading}</span> :
                                        <div className="lds-dual-ring-big" />
                                }
                            </div>
                        ) : ""
                    }
                </div>
            </div>
        );
    }

    private async requestEvents(skipLoading?: boolean) {
        try {
            this.setState({
                events: {},
                isLoading: !(skipLoading === true)
            });

            // Only request the visible range's events
            const beginDate = this.state.displayAfter;
            const endDate = this.state.displayBefore.plus({ millisecond: 1 });

            const parameters = [
                `tz=${encodeURIComponent(TZ())}`,
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`,
                "group=day",
            ];
            const noticeParameters = [
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`,
            ];
            if (this.state.searchPerson) {
                parameters.push(`person=${this.state.searchPerson}`);
            }

            const [json, notices] = await Promise.all([
                apiGet<any>(`fairs/summary?${parameters.join("&")}`),
                V2.get<any[]>(`fairs/notices?${noticeParameters.join("&")}`)
            ]);

            const bins: { [date: number]: Array<{ id: string, title: string, count?: number, type?: string, length?: number }>; } = {};

            const monthBase = DateTime.fromISO(this.state.month);
            for (const group of json.groups) {
                const date = DateTime.fromISO(group.group).diff(monthBase, "days").days + 1;
                if (!bins[date]) {
                    bins[date] = [];
                }

                Array.prototype.push.apply(bins[date], group.items.map((a: any) => ({
                    id: a.id,
                    title: a.name,
                    type: "green"
                })));
            }

            // Insert notices at start of each applicable day
            for (const item of notices) {
                const start = DateTime.fromISO(item.startDate);
                const daysDiff = DateTime.fromISO(item.endDate).diff(start, "days").days;

                for (let i = 0; i < daysDiff;) {
                    const dateOffset = start.plus({ days: i });
                    const date = dateOffset.diff(monthBase, "days").days + 1;
                    const daysLeftInWeek = Math.min(7 - (dateOffset.weekday % 7), daysDiff - i + 1);

                    if (!bins[date]) {
                        bins[date] = [];
                    }

                    bins[date].unshift({
                        id: item.id,
                        title: item.text,
                        type: "orange",
                        length: daysLeftInWeek
                    });

                    i += daysLeftInWeek;
                }
            }

            this.setState({
                events: bins,
                isLoading: false,
                now: new Date(),
            });
        }
        catch (e) {
            // Request failed
            this.setState({
                events: [],
                isLoading: e.message || "Could not load schedule",
                now: new Date(),
            })
        }
    }

    private viewDayDetails(date: string) {
        this.saveSearchState();

        this.props.history.push(`/fairs/schedule/${date}`, {
            searchPerson: this.state.searchPerson,
            selectionMode: this.state.isSelectionMode,
            selectionUrl: this.props.location.state?.selectionUrl,
            selectionState: this.props.location.state?.selectionState,
        });
    }

    private saveSearchState() {
        // Stash the currently displayed month so the user can come back to it
        const currentLocation = this.props.location;
        currentLocation.state = {
            month: this.state.month,
            searchPerson: this.state.searchPerson,
            selectionMode: this.state.isSelectionMode,
            selectionUrl: this.props.location.state?.selectionUrl,
            selectionState: this.props.location.state?.selectionState,
        };
        this.props.history.replace(currentLocation);
    }

    private handleElementChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "month" && !value) {
            return;
        }

        this.setState({
            [name]: value
        }, () => {
            this.saveSearchState();
            if (name !== "month") {
                this.requestEvents();
            }
        });
    }

    private displayRangeChanged(minDate: string, maxDate: string) {
        this.setState({
            displayBefore: DateTime.fromISO(maxDate).toLocal(),
            displayAfter: DateTime.fromISO(minDate).toLocal()
        }, () => this.requestEvents());
    }

    private showEditNoticesDialog() {
        this.setState({
            isEditNoticesDialogOpen: true,
        });
    }

    private closeEditNoticesDialog(success = false) {
        this.setState({
            isEditNoticesDialogOpen: false,
        }, () => { if (success) this.requestEvents() });
    }
}

export default withTitle("Health Fair Schedule", withAuth(HealthFairScheduleView));
export const HealthFairSelectionView = withTitle("Select Health Fair", withAuth(withProps(HealthFairScheduleView, { isSelectionMode: true })));
